/**
 * Google Tag Manager (GTM) integration.
 * Only loads when REACT_APP_GTM_ID is set (e.g. in production).
 */

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
    gtag?: (...args: unknown[]) => void;
  }
}

const GTM_ID = process.env.REACT_APP_GTM_ID?.trim();

/**
 * Initialize GTM. Call once at app startup.
 * No-op if REACT_APP_GTM_ID is not set.
 */
export function initGTM(): void {
  if (!GTM_ID) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(script);

  const noscript = document.createElement('noscript');
  noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${GTM_ID}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
  document.body.insertBefore(noscript, document.body.firstChild);
}

/**
 * Push a page_view event for SPA route changes.
 * Call this whenever the route changes (pathname).
 */
export function pushPageView(path: string): void {
  if (!GTM_ID) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'page_view',
    page_path: path,
  });
}

/**
 * Push AddShoppers/Minty order success event for conversion tracking.
 * Call on order confirmation page load.
 * GTM uses this to fire the Minty Conversion Tag.
 */
export function pushOrderSuccess(data: {
  orderId: string;
  orderSubtotal: number;
  currency?: string;
  userEmail: string;
  offerCode?: string;
}): void {
  if (!GTM_ID) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'addshoppers_order_success',
    // Minty conversion tag expects these names as strings
    order_id: String(data.orderId),
    order_value: String(data.orderSubtotal),
    // Legacy AddShoppers format (keep for backward compatibility)
    addshoppers_order_id: String(data.orderId),
    addshoppers_order_subtotal: data.orderSubtotal,
    addshoppers_currency: data.currency || 'USD',
    addshoppers_user_email: data.userEmail,
    ...(data.offerCode && { addshoppers_offer_code: data.offerCode }),
  });
}

/**
 * Push GA4 purchase event for Google Analytics and Google Ads conversion tracking.
 * Call on order confirmation page load.
 * GTM uses this to fire the GA4 Event tag with ecommerce data.
 * Per Google docs: push ecommerce:null first to clear prior ecommerce data.
 */
export function pushGA4Purchase(data: {
  transactionId: string;
  value: number;
  currency?: string;
  coupon?: string;
  items: Array<{ item_id: string; item_name: string; price: number; quantity: number; index?: number }>;
}): void {
  if (!GTM_ID) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'purchase',
    ecommerce: {
      transaction_id: data.transactionId,
      value: data.value,
      currency: data.currency || 'USD',
      ...(data.coupon && { coupon: data.coupon }),
      items: data.items.map((item, i) => ({
        ...item,
        index: item.index ?? i,
      })),
    },
  });
}

/**
 * Push GA4 add_to_cart event for ecommerce funnel tracking.
 * Call when user adds an item to cart.
 */
export function pushAddToCart(data: {
  currency?: string;
  value: number;
  items: Array<{ item_id: string; item_name: string; price: number; quantity: number; index?: number }>;
}): void {
  if (!GTM_ID) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'add_to_cart',
    ecommerce: {
      currency: data.currency || 'USD',
      value: data.value,
      items: data.items.map((item, i) => ({
        ...item,
        index: item.index ?? i,
      })),
    },
  });
}

/**
 * Push GA4 begin_checkout event for ecommerce funnel tracking.
 * Call when user lands on checkout page.
 */
export function pushBeginCheckout(data: {
  currency?: string;
  value: number;
  items: Array<{ item_id: string; item_name: string; price: number; quantity: number }>;
}): void {
  if (!GTM_ID) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'begin_checkout',
    ecommerce: {
      currency: data.currency || 'USD',
      value: data.value,
      items: data.items.map((item, i) => ({ ...item, index: i })),
    },
  });
}

/**
 * Push form_submit event for lead conversion tracking.
 * Call when user successfully submits a form (e.g. brochure request).
 */
export function pushFormSubmit(data: { formId: string; formName?: string }): void {
  if (!GTM_ID) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'form_submit',
    form_id: data.formId,
    ...(data.formName && { form_name: data.formName }),
  });
}
